import {
  postJsMessage,
  registerSpecificNotificationHandler,
  unregisterSpecificNotificationHandler,
} from '../../bridge';
import API from '../../api';
import { isAvailable } from '../utils';
import { NativeAPIResponseMessage } from '@zoom/hybrid-jsbridge';

export type HistoryData = {
  name: string;
  url: string;
  options: {
    [key: string]: string;
  };
};

export type HistoryPopChangeCallback = (data: any) => void;
let isHistoryPopChangeRegistered = false;
let onHistoryPopChangeFns: HistoryPopChangeCallback[] = [];

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * history.push
 *
 * [PLATFORM]
 * mac        6.2.0+
 * windows    6.2.0+
 * linux      6.2.0+
 * ios        6.2.0+
 * android    6.2.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function push(
  data: HistoryData
): Promise<NativeAPIResponseMessage<HistoryData>> {
  return isAvailable('history.push').then(() => {
    return postJsMessage(API.historyPush, { ...data });
  });
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * history.deleteItem
 *
 * [PLATFORM]
 * mac        6.5.0+
 * windows    6.5.0+
 * linux      6.5.0+
 * ios        6.5.0+
 * android    6.5.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function deleteItem(data: HistoryData) {
  return isAvailable('history.deleteItem').then(() => {
    return postJsMessage(API.historyDeleteItem, { ...data });
  });
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * history.confirmPop
 *
 * [PLATFORM]
 * mac        6.2.0+
 * windows    6.2.0+
 * linux      6.2.0+
 * ios        6.2.0+
 * android    6.2.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function confirmPop(
  data: ConfirmPopData
): Promise<NativeAPIResponseMessage<ConfirmPopData>> {
  return isAvailable('history.confirmPop').then(() => {
    return postJsMessage(API.historyConfirmPop, { ...data });
  });
}

export function onHistoryChange(onHistoryPopChange: HistoryPopChangeCallback) {
  onHistoryPopChangeFns.push(onHistoryPopChange);

  if (!isHistoryPopChangeRegistered) {
    isHistoryPopChangeRegistered = true;
    registerSpecificNotificationHandler(
      HistoryPopNotification,
      onHistoryPopChange
    );
  }
}

export function offHistoryChange(onHistoryPopChange: HistoryPopChangeCallback) {
  onHistoryPopChangeFns = onHistoryPopChangeFns.filter(
    (fn) => fn !== onHistoryPopChange
  );
  if (onHistoryPopChangeFns.length === 0) {
    unregisterSpecificNotificationHandler(
      HistoryPopNotification,
      onHistoryPopChange
    );
    isHistoryPopChangeRegistered = false;
  }
}

export type ConfirmPopData = {
  callId: string;
  name: string;
  url: string;
};
const HistoryPopNotification = 'UnifyNotif_OnHistoryPopChange';

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * history.registerHistoryPopListener
 *
 * [PLATFORM]
 * mac        6.2.0+
 * windows    6.2.0+
 * linux      6.2.0+
 * ios        6.2.0+
 * android    6.2.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function registerHistoryPopListener(
  onHistoryPopChange: HistoryPopChangeCallback
) {
  onHistoryChange(onHistoryPopChange);
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * history.unregisterHistoryPopListener
 *
 * [PLATFORM]
 * mac        6.2.0+
 * windows    6.2.0+
 * linux      6.2.0+
 * ios        6.2.0+
 * android    6.2.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function unregisterHistoryPopListener(
  onHistoryPopChange: HistoryPopChangeCallback
) {
  offHistoryChange(onHistoryPopChange);
}

export default {
  push,
  deleteItem,
  confirmPop,
  registerHistoryPopListener,
  unregisterHistoryPopListener,
  onHistoryChange,
  offHistoryChange,
};
